import {
  Home, KeyRound, FileText as InvoiceIcon, MapPin, Package, FileText,
  History, Mail, RotateCcw, Printer, Users, Settings2, BellDot, Star,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { DashboardCounters } from '../../hooks/useDashboardCounters';
import { richiedeCliente } from '../../utils/contatto';

/**
 * Modello di navigazione dell'Area Cliente, condiviso fra la sidebar
 * desktop e il drawer mobile.
 *
 * Le 13 voci sono raggruppate per intento: piatte erano troppe per una
 * scansione rapida, e il menu risultava alto ~700px spingendo l'`<h1>`
 * della pagina a y = 765px su un viewport di 844.
 *
 * Le etichette NON vanno rinominate: "Messaggi" è concordata con il
 * cliente. Attenzione alla trappola di naming documentata in CONTEXT.md —
 * la rotta `/area-clienti/avvisi` è la pagina *Messaggi*, mentre gli
 * *Avvisi disponibilità* stanno su `/area-clienti/in-attesa`.
 */
export interface AccountNavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  testId: string;
  /** Match anche le sotto-rotte (per evidenziare la voce). */
  exact?: boolean;
  /**
   * Visibile solo al cliente titolare.
   *
   * Il backend richiede esattamente `ruolo == "cliente"`
   * (`clienti/operatori_router.py:49-51`): i sotto-utenti vivono nella
   * collection `operatori` e al login diventano `cliente_subordinato`,
   * mentre un `agente` — che `App.tsx` ammette in questo layout — non è
   * comunque autorizzato. Il filtro va quindi espresso come allow-list di
   * `cliente`, non come deny-list di `cliente_subordinato`: altrimenti
   * l'agente vede la voce e prende 403.
   */
  clienteOnly?: boolean;
  /** Da quale contatore prende il badge, se ne ha uno. */
  badge?: keyof DashboardCounters;
}

export interface AccountNavGroup {
  /** `null` per le voci senza intestazione di gruppo (la panoramica). */
  label: string | null;
  items: AccountNavItem[];
}

export const ACCOUNT_NAV: AccountNavGroup[] = [
  {
    label: null,
    items: [
      { to: '/area-clienti', label: 'Panoramica', icon: Home, testId: 'nav-area-cliente', exact: true },
    ],
  },
  {
    // In cima perché è ciò per cui un cliente B2B entra nell'area
    // riservata: verificare uno stato, scaricare una fattura.
    label: 'Ordini e documenti',
    items: [
      { to: '/area-clienti/ordini', label: 'I miei ordini', icon: Package, testId: 'nav-ordini', badge: 'ordiniAggiornati' },
      { to: '/area-clienti/documenti', label: 'Documenti', icon: FileText, testId: 'nav-documenti' },
      { to: '/area-clienti/storico-acquisti', label: 'Storico acquisti', icon: History, testId: 'nav-storico' },
    ],
  },
  {
    // Attività reattive: "qualcosa non va" / "qualcosa mi deve arrivare".
    label: 'Assistenza',
    items: [
      { to: '/area-clienti/rma', label: 'RMA', icon: RotateCcw, testId: 'nav-rma' },
      { to: '/area-clienti/avvisi', label: 'Messaggi', icon: Mail, testId: 'nav-avvisi', badge: 'messaggiNonLetti' },
      { to: '/area-clienti/in-attesa', label: 'Avvisi disponibilità', icon: BellDot, testId: 'nav-avvisi-stock' },
    ],
  },
  {
    // Cose salvate per ricomprare: alimentano il catalogo, non
    // l'amministrazione. `preferiti` esisteva come rotta raggiungibile
    // solo dall'header: qui entra nel menu.
    label: 'Il mio parco',
    items: [
      { to: '/area-clienti/parco-macchine', label: 'I miei modelli', icon: Printer, testId: 'nav-modelli' },
      { to: '/area-clienti/preferiti', label: 'Preferiti', icon: Star, testId: 'nav-preferiti' },
    ],
  },
  {
    // Configurazione, la parte a più bassa frequenza d'uso: in fondo per scelta.
    label: 'Account e azienda',
    items: [
      { to: '/area-clienti/dati-fatturazione', label: 'Dati di fatturazione', icon: InvoiceIcon, testId: 'nav-dati-fatturazione' },
      { to: '/area-clienti/indirizzi-spedizione', label: 'Indirizzi di spedizione', icon: MapPin, testId: 'nav-indirizzi' },
      { to: '/area-clienti/dati-accesso', label: 'Dati di accesso', icon: KeyRound, testId: 'nav-dati-accesso' },
      { to: '/area-clienti/operatori', label: 'Gestione operatori', icon: Users, testId: 'nav-operatori', clienteOnly: true },
      { to: '/area-clienti/preferenze', label: 'Preferenze', icon: Settings2, testId: 'nav-preferenze' },
    ],
  },
];

/**
 * Applica le regole di visibilità per ruolo a tutti i gruppi. Un Contatto
 * (`utils/contatto.ts`) non vede le pagine che chiedono un Cliente: le
 * ritrova dopo il primo ordine.
 */
export function visibleAccountNav(ruolo: string | undefined, contatto: boolean = false): AccountNavGroup[] {
  return ACCOUNT_NAV
    .map(g => ({
      ...g,
      items: g.items.filter(i => !(i.clienteOnly && ruolo !== 'cliente') && !(contatto && richiedeCliente(i.to))),
    }))
    .filter(g => g.items.length > 0);
}

/**
 * Etichetta della sezione corrente, per la barra sticky che fa da
 * breadcrumb (nell'area riservata non ce n'è uno).
 *
 * Le rotte più specifiche vincono, così `/area-clienti/ordini/OW/2026/...`
 * risolve su "I miei ordini" e non sulla panoramica.
 */
export function currentSectionLabel(pathname: string): string | null {
  const all = ACCOUNT_NAV.flatMap(g => g.items);
  const exact = all.find(i => i.to === pathname);
  if (exact) return exact.label;
  const prefixed = all
    .filter(i => !i.exact && pathname.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return prefixed?.label ?? null;
}
